// @flow

import { has, split } from "lodash";
import type { Orientation, InchRect, Rect, Size } from "types";

import { get_viewport_dimension, invert_when } from "utils/common";
import type { ZoomLevel } from "components/zoom_control";

/* Measurement conversion. The UI displays sizes in metrics or imperial
 * depends on the locale eventually. All internal tracking and pricing
 * are done in imperial, hence the need here to do the conversion. */
const INCH_TABLE = {
  "4": "10",
  "5": "12",
  "6": "15",
  "7": "18",
  "7.5": "19",
  "8": "20",
  "9": "23",
  "10": "25",
  "12": "30",
  "13": "33",
  "14": "35",
  "15": "37",
  "16": "40",
  "18": "45",
  "20": "48",
  "21": "50",
  "24": "60",
  "28": "70",
  "30": "75",
  "32": "80",
  "36": "90",
  "40": "95",
  "42": "100",
  "48": "120",
  "60": "145",
  "62": "150"
};
export function inch_to_cm(inch: string): string {
  if (has(INCH_TABLE, inch)) {
    return INCH_TABLE[inch];
  }
  return parseInt(inch * 2.54).toString();
}

export function size_in_cm(size: string): string {
  const dimensions: Array<string> = split(size, "x");
  return (
    inch_to_cm(dimensions[0]) +
    (dimensions.length === 2 ? "x" + inch_to_cm(dimensions[1]) : "")
  );
}

export function framed_size(size: string): string {
  if (size === "6x18") {
    return "8x24";
  }

  const [w, h] = get_dimensions(size);
  return `${w + 4}x${h + 4}`;
}

export function get_dimensions(
  size: string,
  orientation: Orientation = "portrait"
): [number, number] {
  const dimensions: Array<string> = split(size, "x");
  // All sizes are in portrait mode, like 4x6 or 5x7. So let's swap the
  // 2 dimensions if it's a landscape photo.
  if (orientation === "landscape") {
    return [parseFloat(dimensions[1]), parseFloat(dimensions[0])];
  } else {
    return [parseFloat(dimensions[0]), parseFloat(dimensions[1])];
  }
}

export function get_ratio(size: string): number {
  return size[0] === 'A' ? 1/1.414 : parseFloat(split(size, "x")[0]) / parseFloat(split(size, "x")[1]);
}

/**
 * Return the appropriate aspect ratio for the image. The ratio comes
 * from the `size` but also affected by the natural aspect ratio of the
 * image as well as whether the product enforces a fixed orientation.
 *
 * Ratio will be, by default, less or equal to one. Let's
 * check out the orientation of the photo to decide whether we should
 * change the ratio accordingly.
 *
 * If the product does not allow this flexible orientation, we shouldn't
 * change the ratio.
 *
 * @returns {number}
 */
export function get_ratio_for_img(
  size: string,
  width: number,
  height: number,
  require_fixed_orientation: boolean
): number {
  return invert_when(
    get_ratio(size),
    !(width <= height || require_fixed_orientation)
  );
}

export function is_panorama(size: string): boolean {
  const ratio = get_ratio(size);
  return ratio <= 0.5;
}

/**
 * Take a rect with (x, y, w, h) as well as its container (width, height),
 * turn everything into percentage.
 *
 * For example:
 * (0, 0, 10, 20) in a container (100, 100) will become
 * (0, 0, 10%, 20%)
 *
 * @param rect: (x, y, w, h) in inches or mm.
 * @param width: width of the container
 * @param height: height of container.
 */
export function relativise_rect(
  rect: InchRect,
  width: number,
  height: number
): InchRect {
  let [x, y, w, h] = rect;
  return [x / width, y / height, w / width, h / height];
}

function _to_absolute_px(percent: number, total: number): string {
  return `${parseInt(total * percent)}px`;
}

export function to_absolute(rect: InchRect, width: number, height: number) {
  let [x, y, w, h] = rect;
  return {
    left: _to_absolute_px(x, width),
    top: _to_absolute_px(y, height),
    width: _to_absolute_px(w, width),
    height: _to_absolute_px(h, height)
  };
}

/**
 * Return the largest possible size to fit into a container.
 *
 * Given the container's size and how much margin we'd like to have,
 * this function will find out the rectangular
 * 1. satisfies the `ratio` constraint
 * 2. is the largest possible rectangular.
 *
 * This function used to be called `get_default_crop_rect` when it was used to
 * calculate the default crop rect for an image given aspect ratio;
 *
 * Since then, we have generalised the function in a few other areas. For
 * example, we use this to calculate the maximum container for editor's
 * preview window.
 *
 */
export function get_max_rect_to_fit(
  w: number,
  h: number,
  target_ratio: number
): Rect {
  let crop_rect: Rect = { width: w, height: h, left: 0, top: 0 };
  const original_ratio: number = w / h;

  function vertical_slice(crop_rect): void {
    // height === h and top === 0
    crop_rect.width = h * target_ratio;
    crop_rect.left = (w - crop_rect.width) / 2;
  }

  function horizontal_slice(crop_rect): void {
    // width === w and left === 0
    crop_rect.height = w / target_ratio;
    crop_rect.top = (h - crop_rect.height) / 2;
  }

  if (target_ratio !== original_ratio) {
    if (target_ratio > original_ratio) {
      horizontal_slice(crop_rect);
    } else {
      vertical_slice(crop_rect);
    }
  }

  return crop_rect;
}

export function is_full_frame_ratio(size: Size): boolean {
  let ratio =
    parseFloat(split(size, "x")[1]) <= parseFloat(split(size, "x")[0])
      ? get_ratio(size)
      : 1 / get_ratio(size);
  return 1.5 === ratio; // Ratio of full frame photo is 1.5 (image_width/image_height)
}

export function get_nearest_size(
  size_list: Array<Size>,
  current_size: Size,
  default_size: ?Size
): ?Size {
  //Get list of sizes, current size and returns the nearest matching size in the list.
  //if not return the default value
  return (
    size_list.find(
      size =>
        parseFloat(split(current_size, "x")[0]) <=
        parseFloat(split(size, "x")[0])
    ) || default_size
  );
}

export function calculate_image_size(zoomLevel: ZoomLevel): number {
  let vw = get_viewport_dimension().width;
  let zoom_factor = 1;
  switch (zoomLevel) {
    case "single":
      zoom_factor = 2;
      break;
    case "double":
      zoom_factor = 4;
      break;
    case "triple":
      zoom_factor = 6;
      break;
    case "quadruple":
      zoom_factor = 8;
      break;
    case "quintuple":
      zoom_factor = 10;
      break;
    case "sextuple":
      zoom_factor = 12;
      break;
  }
  return vw / zoom_factor;
}

export function get_orientation(size: string): Orientation {
  const dimensions: Array<string> = split(size, "x");
  return parseInt(dimensions[0]) > parseInt(dimensions[1])
    ? "landscape"
    : "portrait";
}
